// Column — one Kanban column. Owns drag-over visual state and the
// done-collapse behavior for the trailing column.

import type { DragEvent } from 'react';
import { RunCard } from './RunCard';
import type { ColumnDef } from '../lib/columns';
import type { LastEvent, RunVM } from '../lib/types';

interface ColumnProps {
  col: ColumnDef;
  runs: RunVM[];
  hoverActive: boolean;
  wipBlocked: boolean;
  doneExpanded: boolean;
  setDoneExpanded: (v: boolean) => void;
  selectedRunId: string | null;
  draggingRunId: string | null;
  liveEvents: Record<string, LastEvent | null>;
  onOpen: (runId: string) => void;
  onCancel: (runId: string) => void;
  onPromote?: (runId: string) => void;
  onDragStart: (e: DragEvent<HTMLDivElement>, run: RunVM) => void;
  onDragEnd: () => void;
  onDragOver: (e: DragEvent<HTMLDivElement>) => void;
  onDragLeave: () => void;
  onDrop: (e: DragEvent<HTMLDivElement>) => void;
}

export function Column(props: ColumnProps) {
  const { col, runs, hoverActive, wipBlocked, doneExpanded, setDoneExpanded } = props;
  const isDone = col.id === 'done';
  const visibleRuns = isDone && !doneExpanded ? [] : runs;

  return (
    <div
      className={[
        'column',
        `col-${col.id}`,
        hoverActive ? 'drag-over' : '',
        wipBlocked ? 'wip-blocked' : '',
      ].filter(Boolean).join(' ')}
      onDragOver={props.onDragOver}
      onDragLeave={props.onDragLeave}
      onDrop={props.onDrop}
    >
      <div className="col-head">
        <div className="col-head-row">
          <h3 className="col-title">{col.title}</h3>
          <span className="col-count mono">
            {runs.length}{col.limit ? ` / ${col.limit}` : ''}
          </span>
        </div>
        <div className="col-hint mono">{col.hint}</div>
        {wipBlocked && <div className="wip-tip mono">WIP limit reached</div>}
      </div>
      <div className="col-body">
        {isDone && !doneExpanded ? (
          (() => {
            const escalatedCount = runs.filter((r) => r.doneState === 'escalated').length;
            const mergedCount = runs.filter((r) => r.doneState === 'merged').length;
            return (
              <button
                className={`done-collapsed${escalatedCount > 0 ? ' has-escalated' : ''}`}
                onClick={() => setDoneExpanded(true)}
              >
                <span className="mono">{runs.length} closed</span>
                <span className="done-collapsed-detail mono">
                  {mergedCount} merged ·{' '}
                  <span className={escalatedCount > 0 ? 'done-escalated-count' : ''}>
                    {escalatedCount} escalated
                  </span>
                </span>
                <span className="done-expand-cta mono">expand ▸</span>
              </button>
            );
          })()
        ) : (
          <>
            {isDone && doneExpanded && (
              <button className="done-collapse-btn mono" onClick={() => setDoneExpanded(false)}>
                ◂ collapse
              </button>
            )}
            {visibleRuns.map((run) => (
              <RunCard
                key={run.runId}
                run={run}
                selected={props.selectedRunId === run.runId}
                isDragging={props.draggingRunId === run.runId}
                liveEvent={props.liveEvents[run.runId] ?? null}
                onOpen={props.onOpen}
                onCancel={props.onCancel}
                {...(props.onPromote ? { onPromote: props.onPromote } : {})}
                onDragStart={props.onDragStart}
                onDragEnd={props.onDragEnd}
              />
            ))}
            {visibleRuns.length === 0 && !isDone && (
              <div className="col-empty mono">empty</div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
